import clsx from 'clsx';

import { useConnections } from '../hooks/useConnections';
import { usePlatformConnections } from '../hooks/usePlatformConnections';
import type { ModelProviderResponse, PlatformConnectionResponse } from '../api';

interface ProviderSelectProps {
  value: string;
  onChange: (providerId: string) => void;
  disabled?: boolean;
  error?: string | undefined;
}

/** Returns 'Local' for Ollama variants, 'Cloud' for the rest. */
function badgeForProvider(provider: ModelProviderResponse): 'Cloud' | 'Local' {
  const type = provider.type.toLowerCase();
  return type.includes('ollama') || type.includes('local') ? 'Local' : 'Cloud';
}

/**
 * Single-select dropdown of the tenant's model providers, including platform-managed providers
 * eligible under the tenant's current license plan (shown in a separate optgroup).
 * Inactive tenant providers are still listed but disabled (so existing references stay
 * visible without being a valid choice for new drafts).
 */
export function ProviderSelect({
  value,
  onChange,
  disabled = false,
  error,
}: ProviderSelectProps): JSX.Element {
  const { data: tenantProviders, isLoading: tenantLoading, isError: tenantError } = useConnections();
  const { data: platformProviders, isLoading: platformLoading } = usePlatformConnections();

  const isLoading = tenantLoading || platformLoading;

  if (isLoading) {
    return (
      <div className="text-sm text-slate-500">Cargando proveedores configurados…</div>
    );
  }

  if (tenantError || !Array.isArray(tenantProviders)) {
    return (
      <div className="text-sm text-danger">
        No se pudieron cargar los proveedores. Revisa la sección de Configuración.
      </div>
    );
  }

  const activePlatform: PlatformConnectionResponse[] = Array.isArray(platformProviders)
    ? platformProviders
    : [];

  if (tenantProviders.length === 0 && activePlatform.length === 0) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        No tienes proveedores configurados. Crea uno en Configuración → Conexiones
        antes de asignarlo a un tipo de documento.
      </div>
    );
  }

  const selectedInTenant = tenantProviders.find((p) => p.providerId === value);
  const selectedInPlatform = activePlatform.find((p) => p.providerId === value);
  const hasSelected = selectedInTenant !== undefined || selectedInPlatform !== undefined;

  return (
    <div className="space-y-1">
      <select
        className={clsx('input', error !== undefined && 'border-danger focus:border-danger focus:ring-danger')}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      >
        {value === '' || !hasSelected ? (
          <option value="">Selecciona un proveedor…</option>
        ) : null}

        {tenantProviders.length > 0 ? (
          <optgroup label="Mis proveedores">
            {tenantProviders.map((provider) => {
              const badge = badgeForProvider(provider);
              const label = `${provider.alias} — ${provider.modelName ?? provider.type} (${badge})${
                !provider.isActive ? ' · inactivo' : ''
              }`;
              return (
                <option key={provider.providerId} value={provider.providerId} disabled={!provider.isActive}>
                  {label}
                </option>
              );
            })}
          </optgroup>
        ) : null}

        {activePlatform.length > 0 ? (
          <optgroup label="Plataforma (incluidos en tu plan)">
            {activePlatform.map((provider) => {
              const limit = provider.monthlyCallLimit !== null
                ? ` · límite ${provider.monthlyCallLimit.toLocaleString('es-ES')}/mes`
                : '';
              const label = `${provider.displayName} — ${provider.modelName}${limit}`;
              return (
                <option key={provider.providerId} value={provider.providerId}>
                  {label}
                </option>
              );
            })}
          </optgroup>
        ) : null}
      </select>
      {error !== undefined ? <p className="text-xs text-danger">{error}</p> : null}
    </div>
  );
}
